/**
 * Dữ liệu mẫu và máy chủ giả của các màn eKYC (Định danh eKYC).
 *
 * Đóng vai backend khi chưa đăng nhập Keycloak thật hoặc khi URL có `?mock=`.
 *
 * Bộ dữ liệu này **cố ý dựng quanh các nhánh xấu** mà bộ bàn giao `docs/eKYC/` cảnh báo, vì đó là
 * những thứ chỉ lộ ra trên màn hình chứ không lộ ra trong log:
 *
 * - `ek-002` — phiên `VERIFIED` mà **chưa vào sổ định danh** (`07` §23.1). Đã xảy ra thật:
 *   khoá nhà cung cấp hết hạn ⇒ OCR trượt ⇒ phiên vẫn duyệt ⇒ sổ rỗng ⇒ màn thẩm định trông
 *   hoàn toàn bình thường. Phải thấy banner thường trú.
 * - `ek-003` — hạng mục `ERROR` với `score = 0` cạnh một hạng mục `FAIL` cũng `score = 0`
 *   (`04` §1.3). Hai dòng phải đọc ra khác nhau: "chưa chạy được" khác "đã kiểm và không khớp".
 * - `ek-004` — phiên đứng lại vì **máy chủ** hỏng (`systemError`, `07` §22.10): khách thấy
 *   "Hoàn thành" màu xanh trong khi **chưa có gì được kiểm** là điều phải tránh.
 * - `ek-005` — `SKIPPED` vì đối tác **chủ động tắt** hạng mục, và yếu tố rủi ro tương ứng là
 *   `null` chứ không phải `0` (`05` §2.2).
 * - `ek-006` — trùng số định danh (`HR-E05` ⇒ REJECT) và `ek-007` — trùng mặt 1:N (⇒ REVIEW).
 *   Bất đối xứng này là **phát biểu về hiệu chỉnh**, không phải về mức nghiêm trọng (`05` §8.13).
 *
 * Thẻ triển khai mặc định trả `MIXED` + `enforcedBy: 'gateway'`, `selfEnforced: false` — đúng cấu
 * hình rủi ro của `00` §9, để soát được nhánh cảnh báo đỏ trên màn Tổng quan.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. **Không quyết màu hay nhãn ở đây** (CLAUDE.md) — bảng tông
 * nằm ở `modules/ekyc/constants.ts`.
 */
import type { ApiError } from '../lib/errors';
import type {
  AnhDaTai,
  KetQuaThamDinh,
  ThamDinhGui,
  DongNhatKy,
  KetQuaHangMuc,
  MaHangMuc,
  PhienChiTiet,
  PhienTomTat,
  ThongKeEkyc,
  ThongTinGiayTo,
  ToanVenNhatKy,
  Trang,
  TrangThaiPhien,
  TrienKhai,
  VaiTroTaiLen,
  VaiTroTep,
} from '../modules/ekyc/types';

const APP = 'app_4efd8463';

function ket(p: Partial<KetQuaHangMuc> = {}): KetQuaHangMuc {
  return { result: 'PASS', score: 0.9, threshold: 0.7, issues: [], modelVersion: '', elapsedMs: 12, ...p };
}

/** Bộ năm hạng mục đạt sạch, để các phiên chỉ phải khai phần khác biệt. */
function namHangMucDat(): Record<MaHangMuc, KetQuaHangMuc> {
  return {
    sanity: ket({ score: 0.88, threshold: 0.7, elapsedMs: 8 }),
    logic: ket({ score: 1, threshold: null, elapsedMs: 5 }),
    fraud: ket({ score: 0.91, threshold: 0.75, elapsedMs: 248, modelVersion: '' }),
    liveness: ket({ score: 0.94, threshold: 0.8, elapsedMs: 151, modelVersion: 'minifasnet-v2-2026q1' }),
    faceMatch: ket({ score: 0.83, threshold: 0.5, elapsedMs: 302, modelVersion: 'sface-2021dec' }),
  };
}

const KHONG_YEU_TO = {
  document: null,
  ocr: null,
  fraud: null,
  liveness: null,
  pad: null,
  face: null,
  device: null,
  authority: null,
  behavior: null,
} as const;

function giayTo(p: Partial<ThongTinGiayTo> = {}): ThongTinGiayTo {
  return {
    idNumber: '•••••••• 4821',
    oldIdNumber: null,
    fullName: 'Nguyễn Thị Minh Khuê',
    dob: '1992-04-17',
    gender: 'NU',
    issueDate: '2021-06-03',
    expiryDate: '2032-04-17',
    issuePlace: 'Cục Cảnh sát quản lý hành chính về trật tự xã hội',
    residence: 'Số 12, ngõ 45, phường Khương Trung, quận Thanh Xuân, Hà Nội',
    nationality: 'Việt Nam',
    mrz: 'IDVNM••••••4821',
    edited: null,
    // ⚠️ `ocr-service` chỉ trả MỘT độ tin cậy cho cả chứng từ, lưu dưới khoá `'*'` (`01` §5.1).
    confidence: { '*': 0.93 },
    ...p,
  };
}

interface BanGhi {
  tom: PhienTomTat;
  chiTiet: Omit<PhienChiTiet, keyof PhienTomTat | 'review'>;
  nhatKy: DongNhatKy[];
}

function dong(
  sequence: number,
  action: string,
  actor: string,
  at: string,
  hash: string | null = `3f${sequence}a9c1e`.padEnd(16, '0'),
): DongNhatKy {
  return { id: `nk-${sequence}`, sequence, object: 'phien', action, actor, ip: '10.14.2.37', at, hash };
}

const BAN_GHI: BanGhi[] = [
  // ── Phiên đạt sạch, đã vào sổ: đường hạnh phúc để đối chiếu. ──
  {
    tom: {
      id: 'ek-001',
      appId: APP,
      referenceId: 'KH-2026-10-0431',
      documentType: 'VIETNAM_CCCD',
      status: 'VERIFIED',
      decision: 'PASS',
      riskScore: 0.1184,
      riskLevel: 'LOW',
      createdAt: '2026-10-22T09:41:12Z',
      completedAt: '2026-10-22T09:41:17Z',
    },
    chiTiet: {
      checks: namHangMucDat(),
      decisionDetail: {
        status: 'PASS',
        riskLevel: 'LOW',
        riskScore: 0.1184,
        reasonCodes: [],
        hardRules: [],
        riskFactors: {
          ...KHONG_YEU_TO,
          document: 0.12,
          ocr: 0,
          fraud: 0.09,
          liveness: 0.06,
          face: 0.17,
          behavior: 0,
        },
        policyId: 'pol_foxai_default_v1',
      },
      documentInfo: giayTo(),
      fraudRisk: 0.09,
      errorCode: null,
      systemError: false,
      selfieAttempts: 1,
      selfieRetryRemaining: 2,
      registryEnrolled: true,
      processingMs: 4712,
    },
    nhatKy: [
      dong(10241, 'phien.tao', 'api · app_4efd8463', '2026-10-22T09:41:12Z'),
      dong(10242, 'consent.ghi', 'khach', '2026-10-22T09:41:19Z'),
      dong(10243, 'kiem.giay_to', 'he_thong', '2026-10-22T09:41:24Z'),
      dong(10244, 'kiem.sinh_trac', 'he_thong', '2026-10-22T09:41:29Z'),
      dong(10245, 'quyet_dinh.chot', 'he_thong', '2026-10-22T09:41:29Z'),
    ],
  },

  // ── `07` §23.1: ĐÃ DUYỆT mà CHƯA VÀO SỔ — người này đăng ký lại được. ──
  {
    tom: {
      id: 'ek-002',
      appId: APP,
      referenceId: 'KH-2026-10-0429',
      documentType: 'VIETNAM_CCCD',
      status: 'VERIFIED',
      decision: 'PASS',
      riskScore: 0.2317,
      riskLevel: 'LOW',
      createdAt: '2026-10-22T08:12:40Z',
      completedAt: '2026-10-22T08:12:48Z',
    },
    chiTiet: {
      checks: {
        ...namHangMucDat(),
        // OCR hỏng vì khoá nhà cung cấp hết hạn — nhưng phiên vẫn được duyệt.
        logic: ket({
          result: 'ERROR',
          score: 0,
          threshold: null,
          modelVersion: '',
          issues: [
            {
              code: 'E_AI_UNAVAILABLE',
              severity: 'HIGH',
              message: 'Không bóc tách được giấy tờ: nhà cung cấp từ chối khoá API.',
            },
          ],
        }),
      },
      decisionDetail: {
        status: 'PASS',
        riskLevel: 'LOW',
        riskScore: 0.2317,
        reasonCodes: ['RC_AI_UNAVAILABLE'],
        hardRules: [],
        riskFactors: { ...KHONG_YEU_TO, document: 0.14, fraud: 0.11, liveness: 0.08, face: 0.21, behavior: 0 },
        policyId: 'pol_foxai_default_v1',
      },
      documentInfo: null,
      fraudRisk: 0.11,
      errorCode: null,
      systemError: false,
      selfieAttempts: 1,
      selfieRetryRemaining: 2,
      registryEnrolled: false,
      processingMs: 8104,
    },
    nhatKy: [
      dong(10231, 'phien.tao', 'api · app_4efd8463', '2026-10-22T08:12:40Z'),
      dong(10232, 'ocr.loi', 'he_thong', '2026-10-22T08:12:44Z'),
      dong(10233, 'quyet_dinh.chot', 'he_thong', '2026-10-22T08:12:48Z'),
    ],
  },

  // ── `04` §1.3: ERROR và FAIL cạnh nhau, cùng score = 0, phải đọc ra khác nhau. ──
  {
    tom: {
      id: 'ek-003',
      appId: APP,
      referenceId: 'KH-2026-10-0433',
      documentType: 'VIETNAM_CCCD',
      status: 'MANUAL_REVIEW',
      decision: 'REVIEW',
      riskScore: 0.5821,
      riskLevel: 'MEDIUM',
      createdAt: '2026-10-23T14:19:26Z',
      completedAt: '2026-10-23T14:19:33Z',
    },
    chiTiet: {
      checks: {
        sanity: ket({ score: 0.74, threshold: 0.7 }),
        logic: ket({
          result: 'FAIL',
          score: 0,
          threshold: null,
          issues: [
            {
              code: 'LOGIC_ID_GENDER_MISMATCH',
              severity: 'HIGH',
              message: 'Chữ số thứ tư của số định danh không khớp giới tính in trên thẻ.',
            },
            {
              code: 'LOGIC_MRZ_CHECKSUM',
              severity: 'HIGH',
              message: 'Chữ số kiểm tra của dải MRZ không khớp dữ liệu MRZ.',
            },
          ],
        }),
        fraud: ket({ score: 0.81, threshold: 0.75 }),
        // Thiếu tệp model: KHÔNG phải SKIPPED, KHÔNG phải PASS (`00` §4.1).
        liveness: ket({
          result: 'ERROR',
          score: 0,
          threshold: 0.8,
          modelVersion: 'chua-bat',
          issues: [
            {
              code: 'E_AI_UNAVAILABLE',
              severity: 'HIGH',
              message: 'Chưa cấu hình model kiểm thực thể sống trên máy chủ.',
            },
          ],
        }),
        faceMatch: ket({ score: 0.61, threshold: 0.5 }),
      },
      decisionDetail: {
        status: 'REVIEW',
        riskLevel: 'MEDIUM',
        riskScore: 0.5821,
        reasonCodes: ['RC_DATA_MISMATCH', 'RC_AI_UNAVAILABLE', 'RC_DOC_BLUR'],
        // `HR-E02` đóng góp mã lý do RỖNG nên hai danh sách không song ánh (`05` §8.12).
        hardRules: ['HR-E02', 'ADR-07'],
        riskFactors: { ...KHONG_YEU_TO, document: 0.26, ocr: 1, fraud: 0.19, face: 0.39, behavior: 0 },
        policyId: 'pol_foxai_default_v1',
      },
      documentInfo: giayTo({
        fullName: 'Trần Quốc Bảo',
        gender: 'NAM',
        idNumber: '•••••••• 1073',
        edited: { fullName: 'Trần Quốc Bảo' },
      }),
      fraudRisk: 0.19,
      errorCode: null,
      systemError: false,
      selfieAttempts: 2,
      selfieRetryRemaining: 1,
      registryEnrolled: false,
      processingMs: 7230,
    },
    nhatKy: [
      dong(10251, 'phien.tao', 'api · app_4efd8463', '2026-10-23T14:19:26Z'),
      dong(10252, 'kiem.giay_to', 'he_thong', '2026-10-23T14:19:30Z'),
      // Dòng chưa được job nối chuỗi (cron 5 phút) — `hash: null` là CHƯA NỐI, không phải chuỗi hỏng.
      dong(10253, 'quyet_dinh.chot', 'he_thong', '2026-10-23T14:19:33Z', null),
    ],
  },

  // ── `07` §22.10: phiên đứng lại vì MÁY CHỦ hỏng, không phải vì người này đáng ngờ. ──
  {
    tom: {
      id: 'ek-004',
      appId: APP,
      referenceId: 'KH-2026-10-0434',
      documentType: 'VIETNAM_PASSPORT',
      status: 'MANUAL_REVIEW',
      decision: null,
      riskScore: null,
      riskLevel: null,
      createdAt: '2026-10-23T15:02:11Z',
      completedAt: '2026-10-23T15:02:14Z',
    },
    chiTiet: {
      checks: {
        sanity: ket({ score: 0.79, threshold: 0.7 }),
        logic: ket({ result: 'ERROR', score: 0, threshold: null, issues: [
          { code: 'E_AI_QUOTA_EXCEEDED', severity: 'HIGH', message: 'Tổ chức đã hết hạn mức token AI tháng này (đã dùng 1.000.000 / 1.000.000 token). Các chức năng dùng AI tạm dừng tới 00:00 ngày 01/12/2026; các chức năng khác vẫn dùng bình thường. Liên hệ quản trị nền tảng nếu cần nâng hạn mức.' },
        ] }),
        fraud: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        liveness: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        faceMatch: ket({ result: 'SKIPPED', score: 0, threshold: null }),
      },
      /*
       * `01` §4.6: đường hết hạn mức AI cố ý KHÔNG đi qua `_dung_som` — một quyết định là phát
       * biểu về một con người, mà ở đây CHƯA ĐỌC ĐƯỢC GIẤY TỜ NÀO. Nên quyết định để rỗng.
       */
      decisionDetail: null,
      documentInfo: null,
      fraudRisk: null,
      errorCode: 'E_AI_QUOTA_EXCEEDED',
      systemError: true,
      selfieAttempts: 0,
      selfieRetryRemaining: 3,
      registryEnrolled: false,
      processingMs: null,
    },
    nhatKy: [
      dong(10261, 'phien.tao', 'api · app_4efd8463', '2026-10-23T15:02:11Z'),
      dong(10262, 'ocr.het_han_muc', 'he_thong', '2026-10-23T15:02:14Z'),
    ],
  },

  // ── `05` §2.2: đối tác chủ động TẮT hạng mục ⇒ SKIPPED, và yếu tố là `null`, không phải 0. ──
  {
    tom: {
      id: 'ek-005',
      appId: APP,
      referenceId: 'KH-2026-10-0428',
      documentType: 'VIETNAM_CMND',
      status: 'VERIFIED',
      decision: 'PASS',
      riskScore: 0.1902,
      riskLevel: 'LOW',
      createdAt: '2026-10-21T13:45:41Z',
      completedAt: '2026-10-21T13:45:46Z',
    },
    chiTiet: {
      checks: {
        ...namHangMucDat(),
        fraud: ket({ result: 'SKIPPED', score: 0, threshold: null }),
      },
      decisionDetail: {
        status: 'PASS',
        riskLevel: 'LOW',
        riskScore: 0.1902,
        reasonCodes: [],
        hardRules: [],
        // `fraud` là `null`: tắt hạng mục KHÔNG được làm điểm rủi ro thấp đi (`05` §2.2).
        riskFactors: { ...KHONG_YEU_TO, document: 0.15, ocr: 0, liveness: 0.1, face: 0.24, behavior: 0 },
        policyId: 'pol_khach_rut_gon_v2',
      },
      documentInfo: giayTo({ fullName: 'Lê Thị Hồng Vân', idNumber: '••••• 7310', mrz: null }),
      fraudRisk: null,
      errorCode: null,
      systemError: false,
      selfieAttempts: 1,
      selfieRetryRemaining: 2,
      registryEnrolled: true,
      processingMs: 5120,
    },
    nhatKy: [
      dong(10221, 'phien.tao', 'api · app_4efd8463', '2026-10-21T13:45:41Z'),
      dong(10222, 'quyet_dinh.chot', 'he_thong', '2026-10-21T13:45:46Z'),
    ],
  },

  // ── `05` §4.1 HR-E05: trùng SỐ ĐỊNH DANH là tất định ⇒ REJECT. ──
  {
    tom: {
      id: 'ek-006',
      appId: APP,
      referenceId: 'KH-2026-10-0435',
      documentType: 'VIETNAM_CCCD',
      status: 'REJECTED',
      decision: 'REJECT',
      riskScore: 0.7412,
      riskLevel: 'HIGH',
      createdAt: '2026-10-23T16:30:05Z',
      completedAt: '2026-10-23T16:30:11Z',
    },
    chiTiet: {
      checks: namHangMucDat(),
      decisionDetail: {
        status: 'REJECT',
        riskLevel: 'HIGH',
        riskScore: 0.7412,
        reasonCodes: ['RC_IDENTITY_ALREADY_ENROLLED'],
        hardRules: ['HR-E05'],
        riskFactors: { ...KHONG_YEU_TO, document: 0.12, ocr: 0, fraud: 0.1, liveness: 0.07, face: 0.19, behavior: 0.4 },
        policyId: 'pol_foxai_default_v1',
      },
      documentInfo: giayTo({ fullName: 'Phạm Hoàng Nam', gender: 'NAM', idNumber: '•••••••• 9004' }),
      fraudRisk: 0.1,
      errorCode: null,
      systemError: false,
      selfieAttempts: 1,
      selfieRetryRemaining: 2,
      registryEnrolled: false,
      processingMs: 6008,
    },
    nhatKy: [
      dong(10271, 'phien.tao', 'api · app_4efd8463', '2026-10-23T16:30:05Z'),
      dong(10272, 'danh_tinh.trung', 'he_thong', '2026-10-23T16:30:10Z'),
      dong(10273, 'quyet_dinh.chot', 'he_thong', '2026-10-23T16:30:11Z'),
    ],
  },

  // ── `05` §4.1 HR-E05: trùng MẶT 1:N có xác suất ⇒ REVIEW, không REJECT. ──
  {
    tom: {
      id: 'ek-007',
      appId: APP,
      referenceId: 'KH-2026-10-0436',
      documentType: 'VIETNAM_CCCD',
      status: 'MANUAL_REVIEW',
      decision: 'REVIEW',
      riskScore: 0.4903,
      riskLevel: 'MEDIUM',
      createdAt: '2026-10-24T09:48:52Z',
      completedAt: '2026-10-24T09:48:59Z',
    },
    chiTiet: {
      checks: {
        ...namHangMucDat(),
        faceMatch: ket({ result: 'REVIEW', score: 0.54, threshold: 0.5, modelVersion: 'sface-2021dec', issues: [
          { code: 'FACE_LOW_QUALITY', severity: 'MEDIUM', message: 'Ảnh chân dung trên thẻ có chất lượng thấp.' },
        ] }),
      },
      decisionDetail: {
        status: 'REVIEW',
        riskLevel: 'MEDIUM',
        riskScore: 0.4903,
        reasonCodes: ['RC_IDENTITY_ALREADY_ENROLLED', 'RC_FACE_LOW_SIM'],
        hardRules: ['HR-E05'],
        riskFactors: { ...KHONG_YEU_TO, document: 0.18, ocr: 0, fraud: 0.14, liveness: 0.09, face: 0.47, behavior: 0 },
        policyId: 'pol_foxai_default_v1',
      },
      documentInfo: giayTo({ fullName: 'Đặng Thị Thu Hiền', idNumber: '•••••••• 2265' }),
      fraudRisk: 0.14,
      errorCode: null,
      systemError: false,
      selfieAttempts: 3,
      selfieRetryRemaining: 0,
      registryEnrolled: false,
      processingMs: 7441,
    },
    nhatKy: [
      dong(10281, 'phien.tao', 'api · app_4efd8463', '2026-10-24T09:48:52Z'),
      dong(10282, 'danh_tinh.trung_mat', 'he_thong', '2026-10-24T09:48:58Z'),
      dong(10283, 'quyet_dinh.chot', 'he_thong', '2026-10-24T09:48:59Z'),
    ],
  },

  // ── Đang chạy giữa luồng: để soát nhịp hỏi lại tự dừng khi xong. ──
  {
    tom: {
      id: 'ek-008',
      appId: APP,
      referenceId: 'KH-2026-10-0437',
      documentType: 'VIETNAM_CCCD',
      status: 'OCR_PROCESSING',
      decision: null,
      riskScore: null,
      riskLevel: null,
      createdAt: '2026-10-24T09:49:58Z',
      completedAt: null,
    },
    chiTiet: {
      checks: {
        sanity: ket({ score: 0.86, threshold: 0.7 }),
        logic: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        fraud: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        liveness: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        faceMatch: ket({ result: 'SKIPPED', score: 0, threshold: null }),
      },
      decisionDetail: null,
      documentInfo: null,
      fraudRisk: null,
      errorCode: null,
      systemError: false,
      selfieAttempts: 0,
      selfieRetryRemaining: 3,
      registryEnrolled: false,
      processingMs: null,
    },
    nhatKy: [dong(10291, 'phien.tao', 'api · app_4efd8463', '2026-10-24T09:49:58Z')],
  },

  // ── Khách bỏ dở và phiên hết hạn: `roi`, KHÔNG gộp vào `tu-choi` (`08` §6). ──
  {
    tom: {
      id: 'ek-009',
      appId: APP,
      referenceId: 'KH-2026-10-0421',
      documentType: 'VIETNAM_DRIVER_LICENSE',
      status: 'CANCELLED',
      decision: null,
      riskScore: null,
      riskLevel: null,
      createdAt: '2026-10-20T10:03:00Z',
      completedAt: '2026-10-20T10:04:12Z',
    },
    chiTiet: {
      checks: {
        sanity: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        logic: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        fraud: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        liveness: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        faceMatch: ket({ result: 'SKIPPED', score: 0, threshold: null }),
      },
      decisionDetail: null,
      documentInfo: null,
      fraudRisk: null,
      errorCode: null,
      systemError: false,
      selfieAttempts: 0,
      selfieRetryRemaining: 3,
      registryEnrolled: false,
      processingMs: null,
    },
    nhatKy: [
      dong(10201, 'phien.tao', 'api · app_4efd8463', '2026-10-20T10:03:00Z'),
      dong(10202, 'consent.tu_choi', 'khach', '2026-10-20T10:04:12Z'),
    ],
  },
  {
    tom: {
      id: 'ek-010',
      appId: APP,
      referenceId: 'KH-2026-10-0418',
      documentType: 'VIETNAM_CCCD',
      status: 'EXPIRED',
      decision: null,
      riskScore: null,
      riskLevel: null,
      createdAt: '2026-10-19T08:00:00Z',
      completedAt: '2026-10-19T08:30:00Z',
    },
    chiTiet: {
      checks: {
        sanity: ket({ score: 0.72, threshold: 0.7 }),
        logic: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        fraud: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        liveness: ket({ result: 'SKIPPED', score: 0, threshold: null }),
        faceMatch: ket({ result: 'SKIPPED', score: 0, threshold: null }),
      },
      decisionDetail: null,
      documentInfo: null,
      fraudRisk: null,
      errorCode: null,
      systemError: false,
      selfieAttempts: 0,
      selfieRetryRemaining: 3,
      registryEnrolled: false,
      processingMs: null,
    },
    nhatKy: [dong(10191, 'phien.tao', 'api · app_4efd8463', '2026-10-19T08:00:00Z')],
  },
];

const THEO_ID = new Map(BAN_GHI.map((b) => [b.tom.id, b]));

const KHONG_TIM_THAY: ApiError = {
  code: 'E_SESSION_NOT_FOUND',
  message: 'Không tìm thấy hồ sơ định danh này.',
};

/**
 * `02` §7.1: **mọi** lời gọi vào `document-info` và `images/{role}` ghi một dòng nhật ký kèm danh
 * tính người xem. Máy chủ giả giữ đúng hành vi đó để màn thẩm định soát được — đó là thứ duy nhất
 * phân biệt truy cập hợp pháp với tò mò về dữ liệu người khác.
 */
let soThuTuTiepTheo = 10300;
const NHAT_KY_THEM = new Map<string, DongNhatKy[]>();

function ghiNhatKyXem(phienId: string, action: string): void {
  const them = NHAT_KY_THEM.get(phienId) ?? [];
  them.push(dong(soThuTuTiepTheo++, action, 'System Administrator', new Date().toISOString()));
  NHAT_KY_THEM.set(phienId, them);
}


// ----- Luồng định danh tại quầy ------------------------------------------------------------------------------

function loiTrangThai(hienTai: TrangThaiPhien): ApiError {
  return { code: 'E_SESSION_INVALID_STATE', status: 409,
    message: `Hồ sơ đang ở trạng thái ${hienTai}, chưa làm được thao tác này.` };
}

/** Phiên mới tạo ở máy chủ giả: đủ năm hạng mục `SKIPPED`, như backend thật ở đợt chưa có phép kiểm nào. */
function phienMoi(id: string, loai: string, ref: string): BanGhi {
  const skip = () => ket({ result: 'SKIPPED', score: 0, threshold: null, elapsedMs: 0 });
  return {
    tom: {
      id, appId: APP, referenceId: ref, documentType: loai as PhienTomTat['documentType'], status: 'CONSENT_REQUIRED',
      decision: null, riskScore: null, riskLevel: null, createdAt: new Date().toISOString(), completedAt: null,
    },
    chiTiet: {
      checks: { sanity: skip(), logic: skip(), fraud: skip(), liveness: skip(), faceMatch: skip() },
      decisionDetail: null, documentInfo: null, fraudRisk: null, errorCode: null, systemError: false,
      selfieAttempts: 0, selfieRetryRemaining: 3, registryEnrolled: false, processingMs: null,
    },
    nhatKy: [],
  };
}

let soPhienMoi = 0;
/** Kết quả thẩm định tay theo id phiên — như các cột `tham_dinh_*` của `ekyc_phien`. */
const THAM_DINH = new Map<string, KetQuaThamDinh>();
const ANH_DA_GAN = new Map<string, Set<VaiTroTaiLen>>();

function layPhien(id: string): BanGhi {
  const b = THEO_ID.get(id);
  if (!b) throw KHONG_TIM_THAY;
  return b;
}

function chuyen(b: BanGhi, den: TrangThaiPhien, hanhDong: string): PhienTomTat {
  b.tom.status = den;
  b.nhatKy.push(dong(soThuTuTiepTheo++, hanhDong, 'System Administrator', new Date().toISOString(), null));
  return b.tom;
}

export const mayChuEkyc = {
  thongKe(): ThongKeEkyc {
    const xong = BAN_GHI.filter((b) => b.tom.completedAt !== null);
    const tuDong = xong.filter((b) => b.tom.decision === 'PASS' || b.tom.decision === 'REJECT');
    const byStatus: Partial<Record<TrangThaiPhien, number>> = {};
    for (const b of BAN_GHI) byStatus[b.tom.status] = (byStatus[b.tom.status] ?? 0) + 1;

    const demLyDo = new Map<string, number>();
    for (const b of BAN_GHI) {
      for (const ma of b.chiTiet.decisionDetail?.reasonCodes ?? []) {
        demLyDo.set(ma, (demLyDo.get(ma) ?? 0) + 1);
      }
    }

    const msCuaXong = BAN_GHI.map((b) => b.chiTiet.processingMs).filter((n): n is number => n !== null);

    return {
      total: BAN_GHI.length,
      pending: BAN_GHI.filter((b) => b.tom.status === 'MANUAL_REVIEW' || b.tom.status === 'ADDITIONAL_REQUIRED').length,
      // `null` khi chưa hoàn tất hồ sơ nào: hiện gạch dài, KHÔNG phải 0%.
      autoRate: xong.length === 0 ? null : tuDong.length / xong.length,
      avgMs: msCuaXong.length === 0 ? null : Math.round(msCuaXong.reduce((a, b) => a + b, 0) / msCuaXong.length),
      byStatus,
      topReasons: [...demLyDo.entries()]
        .map(([code, count]) => ({ code, count }))
        .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code)),
    };
  },

  /**
   * `00` §9 — cấu hình rủi ro: giao cho gateway kiểm, nhưng không có gateway phía trước. Mọi JWT
   * hợp lệ đang toàn quyền, và chỗ duy nhất nói ra điều đó là thẻ này.
   */
  trienKhai(): TrienKhai {
    return {
      mode: 'MIXED',
      database: 'Database riêng',
      users: 'Tài khoản Native',
      authorization: { enforcedBy: 'gateway', selfEnforced: false },
      documentExtraction: { via: 'openai', label: 'Gọi thẳng model' },
    };
  },

  /**
   * `00` §8 lỗi #1: ở v3, `GET /audit/verify` **luôn 403** vì gateway không khai nhánh `audit`,
   * nên thẻ toàn vẹn nhật ký trên dashboard không bao giờ chạy. Máy chủ giả trả kết quả thật để
   * màn dựng được cả hai nhánh; nhánh "chưa kiểm được" soát bằng `?mock=error`.
   */
  toanVen(): ToanVenNhatKy {
    const tong = BAN_GHI.reduce((n, b) => n + b.nhatKy.length, 0);
    return { ok: true, checked: tong, brokenAt: null };
  },

  danhSach(tab: readonly TrangThaiPhien[] | undefined, tim: string): Trang<PhienTomTat> {
    const khoaTim = tim.trim().toLowerCase();
    const loc = BAN_GHI.filter((b) => {
      if (tab && !tab.includes(b.tom.status)) return false;
      // Như máy chủ: không lọc trạng thái thì hồ sơ đã xoá dữ liệu cá nhân không hiện.
      if (!tab && b.tom.status === 'DELETED') return false;
      // `02` §7.1: tìm kiếm chỉ khớp `referenceId` — tìm theo tên hay số giấy tờ xuyên mọi app
      // "đúng là thứ không nên tồn tại".
      if (khoaTim && !b.tom.referenceId.toLowerCase().includes(khoaTim)) return false;
      return true;
    });
    return { items: loc.map((b) => b.tom), next_cursor: null };
  },

  chiTiet(id: string): PhienChiTiet {
    const b = THEO_ID.get(id);
    if (!b) throw KHONG_TIM_THAY;
    return { ...b.tom, ...b.chiTiet, review: THAM_DINH.get(id) ?? null };
  },

  nhatKy(id: string): DongNhatKy[] {
    const b = THEO_ID.get(id);
    if (!b) throw KHONG_TIM_THAY;
    return [...b.nhatKy, ...(NHAT_KY_THEM.get(id) ?? [])].sort((x, y) => x.sequence - y.sequence);
  },

  /** Mở dữ liệu giấy tờ đầy đủ — để lại vết trong nhật ký. */
  thongTinGiayTo(id: string): ThongTinGiayTo | null {
    const b = THEO_ID.get(id);
    if (!b) throw KHONG_TIM_THAY;
    ghiNhatKyXem(id, 'ho_so.xem_pii');
    return b.chiTiet.documentInfo;
  },

  /**
   * Ảnh stream **qua API**, không bao giờ qua presigned URL MinIO (`02` §7.1): một URL presigned
   * rời khỏi hệ thống là không kiểm soát được, và không ai ghi lại ai đã xem.
   *
   * Máy chủ giả trả một SVG đặc tả chỗ ảnh sẽ nằm — không có ảnh người thật trong kho mã.
   */
  anh(id: string, vaiTro: VaiTroTep): Blob {
    const b = THEO_ID.get(id);
    if (!b) throw KHONG_TIM_THAY;
    ghiNhatKyXem(id, `ho_so.xem_anh.${vaiTro}`);
    /*
     * Màu lấy từ `currentColor` chứ không viết hex: `check:design` chặn hex viết thẳng, và ở đây
     * quy tắc ấy đúng cả về bản chất — ảnh giữ chỗ nên đi theo chủ đề sáng/tối như mọi thứ khác.
     */
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 200" role="img">
      <rect width="320" height="200" fill="currentColor" opacity="0.08"/>
      <text x="160" y="104" font-family="sans-serif" font-size="13" fill="currentColor"
        opacity="0.6" text-anchor="middle">${vaiTro}</text>
    </svg>`;
    return new Blob([svg], { type: 'image/svg+xml' });
  },

  taoPhien(loai: string, ref: string): PhienTomTat {
    const id = `ek-moi-${++soPhienMoi}`;
    const b = phienMoi(id, loai, ref);
    BAN_GHI.unshift(b);
    THEO_ID.set(id, b);
    b.nhatKy.push(dong(soThuTuTiepTheo++, 'ho_so.tao', 'System Administrator', new Date().toISOString(), null));
    return b.tom;
  },

  dongY(id: string, granted: boolean): PhienTomTat {
    const b = layPhien(id);
    if (b.tom.status !== 'CONSENT_REQUIRED') throw loiTrangThai(b.tom.status);
    return chuyen(b, granted ? 'DOCUMENT_REQUIRED' : 'CANCELLED', 'ho_so.consent');
  },

  /** Từ chối như cổng kiểm của backend: ảnh vượt 10 MB. Không đọc được kích thước ảnh ở đây nên trả cố định. */
  taiAnh(vaiTro: VaiTroTaiLen, soByte: number): AnhDaTai {
    if (soByte > 10 * 1024 * 1024) {
      throw { code: 'E_FILE_TOO_LARGE', status: 413, message: 'Ảnh vượt quá 10 MB.' } satisfies ApiError;
    }
    return { fileId: `tep-${++soThuTuTiepTheo}`, role: vaiTro, width: 1280, height: 800,
      expiresAt: new Date(Date.now() + 30 * 60_000).toISOString() };
  },

  ganAnh(id: string, role: VaiTroTaiLen): PhienTomTat {
    const b = layPhien(id);
    if (b.tom.status === 'CONSENT_REQUIRED') {
      throw { code: 'EKYC_CONSENT_REQUIRED', status: 409,
        message: 'Khách chưa đồng ý cho xử lý dữ liệu, chưa nhận được ảnh giấy tờ.' } satisfies ApiError;
    }
    if (b.tom.status !== 'DOCUMENT_REQUIRED') throw loiTrangThai(b.tom.status);
    const da = ANH_DA_GAN.get(id) ?? new Set<VaiTroTaiLen>();
    da.add(role);
    ANH_DA_GAN.set(id, da);
    b.nhatKy.push(dong(soThuTuTiepTheo++, 'ho_so.gan_tep', 'System Administrator', new Date().toISOString(), null));
    return b.tom;
  },

  nop(id: string): PhienTomTat {
    const b = layPhien(id);
    if (b.tom.status !== 'DOCUMENT_REQUIRED') throw loiTrangThai(b.tom.status);
    if (!ANH_DA_GAN.get(id)?.size) throw loiTrangThai(b.tom.status);
    return chuyen(b, 'MANUAL_REVIEW', 'ho_so.nop');
  },

  /** Như `parseThamDinh` + `thamDinh` của máy chủ: chỉ trên hồ sơ chờ người, từ chối đòi lý do, bổ sung đòi mục. */
  thamDinh(id: string, du: ThamDinhGui): PhienTomTat {
    const b = layPhien(id);
    if (b.tom.status !== 'MANUAL_REVIEW') throw loiTrangThai(b.tom.status);
    const truong: { pointer: string; detail: string }[] = [];
    if (du.decision === 'REJECTED' && !du.note.trim()) {
      truong.push({ pointer: '#/note', detail: 'Từ chối phải ghi lý do: đây là dòng người khiếu nại sau này sẽ đọc.' });
    }
    if (du.decision === 'ADDITIONAL_REQUIRED' && du.requestedItems.length === 0) {
      truong.push({ pointer: '#/requestedItems', detail: 'Phải nêu rõ khách cần nộp thêm gì.' });
    }
    if (truong.length) {
      throw { code: 'INVALID_ARGUMENT', status: 400, message: 'Dữ liệu thẩm định không hợp lệ.', details: truong } satisfies ApiError;
    }
    const luc = new Date().toISOString();
    THAM_DINH.set(id, { decision: du.decision, note: du.note.trim(), requestedItems: du.requestedItems,
      reviewerId: 'nguoi-tham-dinh-mau', at: luc });
    if (du.decision === 'ADDITIONAL_REQUIRED') {
      b.tom.decision = null;
      b.tom.completedAt = null;
    } else {
      b.tom.decision = du.decision === 'VERIFIED' ? 'PASS' : 'REJECT';
      b.tom.completedAt = luc;
    }
    return chuyen(b, du.decision, 'ho_so.tham_dinh');
  },
  huy(id: string): PhienTomTat {
    const b = layPhien(id);
    if (['VERIFIED', 'REJECTED', 'MANUAL_REVIEW', 'EXPIRED', 'CANCELLED', 'DELETED'].includes(b.tom.status)) {
      throw loiTrangThai(b.tom.status);
    }
    b.tom.completedAt = new Date().toISOString();
    return chuyen(b, 'CANCELLED', 'ho_so.huy');
  },
  /** Như `xoaDuLieuCaNhan` của máy chủ: chỉ hồ sơ đã chốt, dòng hồ sơ ở lại với trạng thái `DELETED`. */
  xoa(id: string): void {
    const b = layPhien(id);
    if (!['MANUAL_REVIEW', 'VERIFIED', 'REJECTED', 'EXPIRED', 'CANCELLED', 'ERROR'].includes(b.tom.status)) {
      throw loiTrangThai(b.tom.status);
    }
    // Như máy chủ: xoá dấu vết còn lần ra người, dòng hồ sơ ở lại không còn mã của đối tác.
    b.tom.referenceId = '';
    chuyen(b, 'DELETED', 'ho_so.xoa');
  },
};
